import { Frequency } from '@core/referential/frequency.enum'
import { UseCase } from '@core/referential/use-case.enum'
import { createFileRoute, redirect } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { z } from 'zod'

import PendingComponent from '@/components/pending-component'
import { defaultAvailableUnits, useAuth } from '@/hooks/use-auth'
import { parseDateRange } from '@/lib/date'
import { isUseCase } from '@/lib/utils'
import { siteDjuYearQuery } from '@/queries/site-dju-year.query'

import { getDjuMinMaxYear } from '../-utils/dju.utils'

export const AllowedFrequencies = [Frequency.DAILY, Frequency.WEEKLY, Frequency.MONTHLY, Frequency.YEARLY] as const

export const defaultDateRange = parseDateRange({
  startDate: dayjs().startOf('year').toDate(),
  endDate: dayjs().startOf('month').subtract(1, 'day').toDate(),
})

export const SearchSchema = z.object({
  startDate: z.string().default(defaultDateRange.startDate),
  endDate: z.string().default(defaultDateRange.endDate),
  sites: z.array(z.string()).optional(),
  buildings: z.array(z.string()).optional(),
  filterBy: z.enum(['site', 'building', 'energy']).default('site'),
  energyByDJU: z.boolean().default(false),
  selectedId: z.string().optional(),
  evolutionFilterBy: z.enum(['site', 'building', 'energy']).default('energy'),
  showDJU: z.union([z.literal('none'), z.literal('currentPeriod'), z.literal('previousPeriod')]).default('none'),
  frequency: z.enum(AllowedFrequencies).default(Frequency.MONTHLY),
})

export type Search = z.infer<typeof SearchSchema>

export const Route = createFileRoute('/_dashboard/energy-usage/location-analysis/')({
  validateSearch: SearchSchema,
  async beforeLoad({ context, search }) {
    if (!isUseCase(UseCase.CONSUMPTIONS)) {
      throw redirect({
        to: '/403',
        search: { reason: 'wrongUseCase' },
      })
    }

    const { setCurrentUnitType, setAvailableUnits, user } = useAuth.getState()

    setAvailableUnits(defaultAvailableUnits)

    if (user?.currentUsageType && !defaultAvailableUnits[user.currentUsageType].includes(user.currentUnitType)) {
      setCurrentUnitType(defaultAvailableUnits[user.currentUsageType][0])
    }

    const siteDjuYear = await context.queryClient.fetchQuery(siteDjuYearQuery())

    const { maxYear } = getDjuMinMaxYear({
      djuYears: siteDjuYear,
    })

    if (maxYear < dayjs(search.startDate).year()) {
      throw redirect({
        to: Route.fullPath,
        search: {
          ...search,
          startDate: dayjs().year(maxYear).month(10).format('YYYY-MM-DD'),
          endDate: dayjs().year(maxYear).endOf('year').format('YYYY-MM-DD'),
        },
      })
    }
  },
  pendingComponent: PendingComponent,
})
